import React, { useState, useMemo } from 'react';
import { Layers, Table2 } from 'lucide-react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useComponentDataStore } from '@/stores/editor/component-data-store';
import {
  getModalConfig,
  ModalPreviewOverlay,
  type ModalConfig,
} from '@/components/shared/rendering/ContainerModalWrapper';
import { SharedComponentRenderer } from '@/components/shared/rendering/SharedComponentRenderer';
import { TableRowDataEditor } from '@/components/tables/TableRowDataEditor';
import { useTableModalPreview } from '@/components/tables/table-modal-preview-context';
import { createTableRowEditorFields } from '@/components/tables/table-row-editor-fields';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Component, ComponentType, TableColumn, Viewport } from '@/types';

const EMPTY_COMPONENTS: Component[] = [];

type TableRowData = Record<string, unknown> & {
  data?: Record<string, unknown>;
};

interface TableModalPreviewEntry {
  id: string;
  component: Component;
  mode: 'create' | 'edit';
  label: string;
  description: string;
  fields: ReturnType<typeof createTableRowEditorFields>;
  rowData: Record<string, unknown>;
  readOnly: boolean;
}

interface ModalPreviewEntry {
  component: Component;
  config: ModalConfig;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isTableRowData = (value: unknown): value is TableRowData => isRecord(value);

const normalizeTableRow = (row: TableRowData): Record<string, unknown> => {
  if (!isRecord(row.data)) return row;
  const { data, ...rest } = row;
  return {
    ...data,
    ...rest,
  };
};

const getTableRows = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter(isTableRowData).map(normalizeTableRow)
    : [];

const isTableColumn = (value: unknown): value is TableColumn =>
  isRecord(value) &&
  (value.id === undefined || typeof value.id === 'string') &&
  typeof value.accessorKey === 'string' &&
  typeof value.header === 'string';

const getTableColumns = (value: unknown): TableColumn[] =>
  Array.isArray(value) ? value.filter(isTableColumn) : [];

const isTableComponent = (component: Component): boolean =>
  component.type === ComponentType.TABLE ||
  component.type === ComponentType.ADVANCED_TABLE ||
  component.properties?.advanced === true;

/**
 * Canvas header button that shows modal count and allows previewing modals.
 * Only visible when the current page has containers with properties.modal set.
 */
export const ModalPreviewButton: React.FC = () => {
  const [previewingModalId, setPreviewingModalId] = useState<string | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const {
    activeRequest: activeTablePreview,
    closePreview: closeTablePreview,
    openPreview: openTablePreview,
  } = useTableModalPreview();
  const componentData = useComponentDataStore((s) => s.data);

  const components = useEditorStore((s) => {
    const pageId = s.currentPageId;
    if (!pageId) return EMPTY_COMPONENTS;
    return s.pageComponents.get(pageId) || EMPTY_COMPONENTS;
  });

  const modalEntries = useMemo<ModalPreviewEntry[]>(() => {
    return components.flatMap((component) => {
      const config = getModalConfig(component);
      return config ? [{ component, config }] : [];
    });
  }, [components]);

  const tableModalEntries = useMemo<TableModalPreviewEntry[]>(() => {
    return components.flatMap((component) => {
      if (!isTableComponent(component)) return [];

      const columns = getTableColumns(component.properties?.columns);
      if (columns.length === 0) return [];

      const storeRows = getTableRows(componentData[component.id]);
      const staticRows = getTableRows(component.properties?.data);
      const rows = storeRows.length > 0 ? storeRows : staticRows;
      const sampleRow = rows[0] ?? {};
      const fields = createTableRowEditorFields(columns, rows, sampleRow);
      if (fields.length === 0) return [];

      const componentName = component.name || 'Table';
      const entries: TableModalPreviewEntry[] = [];

      entries.push({
        id: `${component.id}:view-row`,
        component,
        mode: 'edit',
        label: `${componentName}: View Row`,
        description: rows.length > 0 ? 'Preview row details' : 'Preview empty row details',
        fields,
        rowData: sampleRow,
        readOnly: true,
      });

      if (component.properties?.dataAdditionsEnabled === true) {
        entries.push({
          id: `${component.id}:add-row`,
          component,
          mode: 'create',
          label: `${componentName}: Add Row`,
          description: 'Preview the add-row form',
          fields,
          rowData: {},
          readOnly: true,
        });
      }

      return entries;
    });
  }, [componentData, components]);

  const modalCount = modalEntries.length + tableModalEntries.length;

  if (modalCount === 0) return null;

  const previewingEntry = previewingModalId
    ? modalEntries.find((entry) => entry.component.id === previewingModalId)
    : null;
  const previewingComponent = previewingEntry?.component ?? null;
  const previewingConfig = previewingEntry?.config ?? null;
  const activeTablePreviewTitle = activeTablePreview
    ? `${activeTablePreview.componentName}: ${activeTablePreview.mode === 'create' ? 'Add Row' : 'View Row'}`
    : 'Table Row';

  const handleCloseTablePreview = () => {
    activeTablePreview?.onClose();
    closeTablePreview();
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-6 gap-1 ml-3 text-xs border-primary/30 bg-primary/5 hover:bg-primary/10"
          >
            <Layers className="h-3 w-3" />
            <span>{modalCount} {modalCount === 1 ? 'Modal' : 'Modals'}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0"
          align="start"
          sideOffset={8}
        >
          <div className="p-3 border-b">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Page Modals
            </h4>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {modalEntries.map(({ component, config }) => {
              return (
                <div
                  key={component.id}
                  className="flex items-center justify-between px-3 py-2.5 hover:bg-muted/50 border-b last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {config.title}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      Trigger: &quot;{config.triggerText}&quot;
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 ml-2 text-xs shrink-0"
                    onClick={() => {
                      setPreviewingModalId(component.id);
                      setPopoverOpen(false);
                    }}
                  >
                    Preview
                  </Button>
                </div>
              );
            })}
            {tableModalEntries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between px-3 py-2.5 hover:bg-muted/50 border-b last:border-b-0"
              >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <Table2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {entry.label}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {entry.description}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 ml-2 text-xs shrink-0"
                  onClick={() => {
                    setPreviewingModalId(null);
                    openTablePreview({
                      id: entry.id,
                      componentId: entry.component.id,
                      componentName: entry.component.name || 'Table',
                      mode: entry.mode,
                      fields: entry.fields,
                      rowData: entry.rowData,
                      readOnly: entry.readOnly,
                      onClose: () => undefined,
                      onSave: () => undefined,
                    });
                    setPopoverOpen(false);
                  }}
                >
                  Preview
                </Button>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Modal Preview Overlay */}
      {previewingComponent && previewingConfig && (
        <ModalPreviewOverlay
          modalConfig={previewingConfig}
          componentId={previewingComponent.id}
          onClose={() => setPreviewingModalId(null)}
        >
          <ModalPreviewContent
            component={previewingComponent}
            allComponents={components}
          />
        </ModalPreviewOverlay>
      )}

      {activeTablePreview && (
        <ModalPreviewOverlay
          modalConfig={{
            enabled: true,
            triggerText: 'Preview',
            title: activeTablePreviewTitle,
            size: 'lg',
            showBackdrop: true,
            closeOnBackdropClick: true,
            showCloseButton: true,
          }}
          componentId={activeTablePreview.componentId}
          onClose={handleCloseTablePreview}
        >
          <TableRowDataEditor
            open
            embedded
            mode={activeTablePreview.mode}
            fields={activeTablePreview.fields}
            rowData={activeTablePreview.rowData}
            mobileViewport={activeTablePreview.mobileViewport}
            readOnly={activeTablePreview.readOnly}
            showHeader={false}
            onOpenChange={(open) => {
              if (!open) handleCloseTablePreview();
            }}
            onSave={activeTablePreview.onSave}
            onDelete={activeTablePreview.onDelete}
          />
        </ModalPreviewOverlay>
      )}
    </>
  );
};

/**
 * Renders a modal container's children for the preview overlay.
 */
const ModalPreviewContent: React.FC<{
  component: Component;
  allComponents: Component[];
}> = ({ component, allComponents }) => {
  const childComponents = useMemo(() => {
    if (component._childComponents && component._childComponents.length > 0) {
      return component._childComponents;
    }
    if (component.children?.length > 0) {
      return component.children.flatMap((childId) => {
        const child = allComponents.find((candidate) => candidate.id === childId);
        return child ? [child] : [];
      });
    }
    return allComponents.filter((c) => c.parentId === component.id);
  }, [component, allComponents]);

  if (childComponents.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        No form fields in this modal yet
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {childComponents.map((child: Component) => (
        <SharedComponentRenderer
          key={child.id}
          component={child}
          allComponents={allComponents}
          viewport={Viewport.DESKTOP}
        />
      ))}
    </div>
  );
};

export default ModalPreviewButton;
